import React, { useMemo, useState, useEffect } from 'react'
import styled from 'styled-components'
import {
  FaCheckCircle,
  FaRobot,
  FaUser,
  FaChartBar,
  FaLightbulb,
  FaBullseye,
  FaBrain,
  FaHandPointer,
} from 'react-icons/fa'

const ProgressCard = styled.div`
  background: #2a2a2a;
  border-radius: 12px;
  padding: 1.5rem;
  border: 1px solid rgba(147, 51, 234, 0.2);
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  overflow-y: auto;

  &:hover {
    border-color: rgba(147, 51, 234, 0.3);
  }
`

const CardHeader = styled.div`
  text-align: center;
  padding-bottom: 1rem;
  border-bottom: 1px solid rgba(147, 51, 234, 0.1);
`

const CardTitle = styled.h3`
  color: #ffffff;
  font-size: 1.1rem;
  font-weight: 600;
  margin: 0 0 0.5rem 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
`

const CardSubtitle = styled.p`
  color: #b0b0b0;
  font-size: 0.85rem;
  margin: 0;
  line-height: 1.4;
`

const Section = styled.div`
  flex-shrink: 0;
`

const SectionTitle = styled.h4`
  color: #ffffff;
  font-size: 0.9rem;
  font-weight: 600;
  margin: 0 0 0.75rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

const ProgressBarContainer = styled.div`
  margin-bottom: 1rem;
`

const ProgressBar = styled.div`
  background: #404040;
  border-radius: 8px;
  height: 8px;
  overflow: hidden;
  margin-bottom: 0.5rem;
`

const ProgressFill = styled.div<{ $percentage: number; $color?: string }>`
  background: ${(props) => props.$color || 'linear-gradient(90deg, #9333ea 0%, #c084fc 100%)'};
  height: 100%;
  width: ${(props) => props.$percentage}%;
  transition: width 0.3s ease;
  border-radius: 8px;
`

const ProgressText = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 0.8rem;
  color: #b0b0b0;
`

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
  margin-bottom: 1rem;
`

const StatItem = styled.div`
  background: #333333;
  border-radius: 8px;
  padding: 0.75rem;
  text-align: center;
  border: 1px solid rgba(147, 51, 234, 0.1);
`

const StatValue = styled.div`
  color: #ffffff;
  font-size: 1.2rem;
  font-weight: 700;
  margin-bottom: 0.25rem;
`

const StatLabel = styled.div`
  color: #b0b0b0;
  font-size: 0.7rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

const InfoBox = styled.div<{ $type?: 'tip' | 'warning' | 'info' }>`
  background: ${(props) => {
    switch (props.$type) {
      case 'tip':
        return 'rgba(16, 185, 129, 0.1)'
      case 'warning':
        return 'rgba(255, 193, 7, 0.1)'
      default:
        return 'rgba(147, 51, 234, 0.1)'
    }
  }};
  border: 1px solid
    ${(props) => {
      switch (props.$type) {
        case 'tip':
          return 'rgba(16, 185, 129, 0.3)'
        case 'warning':
          return 'rgba(255, 193, 7, 0.3)'
        default:
          return 'rgba(147, 51, 234, 0.3)'
      }
    }};
  border-radius: 8px;
  padding: 0.75rem;
  margin-bottom: 1rem;
`

const InfoText = styled.p`
  color: #e0e0e0;
  font-size: 0.8rem;
  margin: 0;
  line-height: 1.4;

  strong {
    color: #ffffff;
  }
`

const ConfidenceBreakdown = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

const ConfidenceItem = styled.div<{ $color: string }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem;
  background: rgba(60, 60, 60, 0.3);
  border-radius: 6px;
  border-left: 3px solid ${(props) => props.$color};
`

const ConfidenceName = styled.span`
  color: #e0e0e0;
  font-size: 0.8rem;
  font-weight: 500;
`

const ConfidenceCount = styled.span`
  color: #ffffff;
  font-size: 0.8rem;
  font-weight: 600;
`

const TimeDisplay = styled.div`
  text-align: center;
  color: #9333ea;
  font-size: 0.9rem;
  font-weight: 600;
`

interface ReviewProgressCardProps {
  confidenceDistribution: {
    none: number
    high: number
    medium: number
    low: number
  } | null
  totalImages: number
  batchStartTime?: string
  batchStatus?: string
  originalUnlabeledCount: number
  completedCount: number
}

const ReviewProgressCard: React.FC<ReviewProgressCardProps> = ({
  confidenceDistribution,
  totalImages,
  batchStartTime,
  batchStatus,
  originalUnlabeledCount,
  completedCount,
}) => {
  const [elapsedTime, setElapsedTime] = useState('')

  // Calculate elapsed time since batch started
  useEffect(() => {
    if (!batchStartTime) return

    const updateElapsedTime = () => {
      const start = new Date(batchStartTime)
      const now = new Date()
      const diffMs = now.getTime() - start.getTime()

      const hours = Math.floor(diffMs / (1000 * 60 * 60))
      const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60))

      if (hours > 0) {
        setElapsedTime(`${hours}h ${minutes}m`)
      } else {
        setElapsedTime(`${minutes}m`)
      }
    }

    updateElapsedTime()
    const interval = setInterval(updateElapsedTime, 60000) // Update every minute

    return () => clearInterval(interval)
  }, [batchStartTime])

  // Calculate progress and accuracy metrics
  const progressStats = useMemo(() => {
    const total = confidenceDistribution
      ? confidenceDistribution.none +
        confidenceDistribution.low +
        confidenceDistribution.medium +
        confidenceDistribution.high
      : 0

    const aiLabeledImages = confidenceDistribution
      ? confidenceDistribution.low + confidenceDistribution.medium + confidenceDistribution.high
      : 0

    const needsReview = total // All images still in tabs need review
    const humanReviewed = originalUnlabeledCount - needsReview

    // Calculate human review goal (10% of original images)
    const reviewGoal = Math.ceil(originalUnlabeledCount * 0.1)
    const reviewProgress = reviewGoal > 0 ? (Math.max(0, humanReviewed) / reviewGoal) * 100 : 0

    return {
      total,
      aiLabeledImages,
      needsReview,
      humanReviewed: Math.max(0, humanReviewed), // Ensure non-negative
      reviewGoal,
      reviewProgress: Math.min(100, reviewProgress), // Cap at 100%
      highQuality: confidenceDistribution?.high || 0,
    }
  }, [confidenceDistribution, originalUnlabeledCount, completedCount])

  return (
    <ProgressCard>
      <CardHeader>
        <CardTitle>
          <FaUser />
          Review Progress
        </CardTitle>
        <CardSubtitle>Help improve the AI by correcting predictions</CardSubtitle>
      </CardHeader>

      <Section>
        <SectionTitle>
          <FaBullseye />
          Review Goal
        </SectionTitle>
        <ProgressBarContainer>
          <ProgressBar>
            <ProgressFill
              $percentage={progressStats.reviewProgress}
              $color={
                progressStats.reviewProgress >= 100
                  ? 'linear-gradient(90deg, #10b981 0%, #059669 100%)'
                  : undefined
              }
            />
          </ProgressBar>
          <ProgressText>
            <span>{progressStats.humanReviewed} reviewed</span>
            <span>{progressStats.reviewGoal} goal (10%)</span>
          </ProgressText>
        </ProgressBarContainer>
        {progressStats.reviewProgress >= 100 && (
          <TimeDisplay style={{ color: '#10b981' }}>
            <FaCheckCircle style={{ marginRight: '0.5rem' }} />
            Goal achieved! Ready for Fine-Tune
          </TimeDisplay>
        )}
      </Section>

      <Section>
        <SectionTitle>
          <FaChartBar />
          Dataset Statistics
        </SectionTitle>
        <StatsGrid>
          <StatItem>
            <StatValue>{originalUnlabeledCount}</StatValue>
            <StatLabel>Original</StatLabel>
          </StatItem>
          <StatItem>
            <StatValue>{progressStats.aiLabeledImages}</StatValue>
            <StatLabel>AI Labeled</StatLabel>
          </StatItem>
          <StatItem>
            <StatValue>{progressStats.needsReview}</StatValue>
            <StatLabel>Needs Review</StatLabel>
          </StatItem>
          <StatItem>
            <StatValue>{progressStats.humanReviewed}</StatValue>
            <StatLabel>Human Reviewed</StatLabel>
          </StatItem>
        </StatsGrid>
      </Section>

      <Section>
        <SectionTitle>
          <FaBrain />
          Confidence Breakdown
        </SectionTitle>
        <ConfidenceBreakdown>
          <ConfidenceItem $color="#dc3545">
            <ConfidenceName>None (Needs Labels)</ConfidenceName>
            <ConfidenceCount>{confidenceDistribution?.none || 0}</ConfidenceCount>
          </ConfidenceItem>
          <ConfidenceItem $color="#fd7e14">
            <ConfidenceName>Low (Review First)</ConfidenceName>
            <ConfidenceCount>{confidenceDistribution?.low || 0}</ConfidenceCount>
          </ConfidenceItem>
          <ConfidenceItem $color="#ffc107">
            <ConfidenceName>Medium (Verify)</ConfidenceName>
            <ConfidenceCount>{confidenceDistribution?.medium || 0}</ConfidenceCount>
          </ConfidenceItem>
          <ConfidenceItem $color="#198754">
            <ConfidenceName>High (Likely Correct)</ConfidenceName>
            <ConfidenceCount>{confidenceDistribution?.high || 0}</ConfidenceCount>
          </ConfidenceItem>
        </ConfidenceBreakdown>
      </Section>

      <InfoBox $type="tip">
        <InfoText>
          <strong>
            <FaLightbulb style={{ marginRight: '0.5rem' }} />
            Pro Tip:
          </strong>
          Focus on <strong>None</strong> and <strong>Low</strong> confidence images first. These
          corrections will have the biggest impact on model improvement.
        </InfoText>
      </InfoBox>

      <InfoBox $type="info">
        <InfoText>
          <strong>
            <FaHandPointer style={{ marginRight: '0.5rem' }} />
            How it works:
          </strong>
          Click images to label or correct AI predictions. Your corrections create better training
          data for the next model version.
        </InfoText>
      </InfoBox>

      <InfoBox $type="warning">
        <InfoText>
          <strong>
            <FaRobot style={{ marginRight: '0.5rem' }} />
            Remember:
          </strong>
          Every correction you make helps the AI learn and perform better on future images.
        </InfoText>
      </InfoBox>
    </ProgressCard>
  )
}

export default ReviewProgressCard
